import { formatDistanceToNow } from 'date-fns';
import styled, { css } from 'styled-components';

import Icon from '../common/base/Icon';
import Text from '../common/base/Text';
import Tooltip from '../common/base/Tooltip';
import { Div } from '../common/helpers/StyledUtils';
import { FC, ReactNode, useContext } from 'react';
import Link from '../common/base/Link';
import { DashboardContext } from '../scenes/DashboardScene/DashboardContext';
import { capitalizeFirstLetter } from '../../utils';
import CopyableText from '../common/CopyableText';
import { formatDate, getTimezoneAbbreviation } from '../../utils/date';
import { CONTAINER_WIDTHS } from '@hookdeck/theme';
import { useDatePreferences } from '../hooks/useDatePreferences';

export const DASHBOARD_NAV_WIDTH = 224;
export const DASHBOARD_NAV_COLLAPSED_WIDTH = 44;

export const DashboardLayout = styled.div<{ nav_collapsed: boolean; banner_active: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.base.background};
  min-height: ${({ banner_active }) => (banner_active ? `calc(100vh - 32px)` : '100vh')};
  display: grid;
  grid-template-columns: ${({ theme, nav_collapsed }) =>
    nav_collapsed
      ? `${theme.pxToRem(DASHBOARD_NAV_COLLAPSED_WIDTH)} 1fr`
      : `${theme.pxToRem(DASHBOARD_NAV_WIDTH)} 1fr`};
`;

export const DashboardLayoutContent = styled.div<{ banner_active: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.base.background};
  min-height: ${({ banner_active }) => (banner_active ? `calc(100vh - 32px)` : '100vh')};
  flex-grow: 1;
  min-width: 0;
  position: relative;
  top: 0px;
`;

export const StyledViewLayout = styled.div`
  display: flex;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.surface.base.surface};
`;

export const StyledViewNav = styled(Div)<{ relative?: boolean }>(({ theme, relative = false }) => {
  const { is_degraded_banner_active } = useContext(DashboardContext);

  return css`
    position: ${relative ? 'relative' : 'sticky'};
    top: ${is_degraded_banner_active ? '32px' : 0};
    flex-shrink: 0;
    background-color: ${theme.colors.surface.base.surface};
    z-index: ${theme.zindex.sticky};
  `;
});

export const StyledViewNavHeader = styled(Div)(
  ({ theme }) => css`
    padding: ${theme.spacing(0)} ${theme.spacing(6)} ${theme.spacing(0)} ${theme.spacing(6)};
    border-bottom: ${theme.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(4)};
    flex-shrink: 0;
    height: 53px;
    box-sizing: border-box;
    max-width: 100%;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
);

export const StyledViewNavSecondary = styled(Div)(
  ({ theme }) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(6)} ${theme.spacing(0)} ${theme.spacing(6)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    box-sizing: border-box;
    border-bottom: ${theme.border};
  `,
);

export const ViewBreadcrumbsTitle: FC<{
  breadcrumbs: {
    label: string;
    to?: string;
    highlight?: boolean;
    ellipsis?: boolean;
    max_width_vw?: number;
  }[];
}> = ({ breadcrumbs }) => {
  return (
    <Div flex={{ align: 'center', gap: 2 }} style={{ overflow: 'hidden', minWidth: 0 }}>
      {breadcrumbs.map(({ label, to, highlight, ellipsis, max_width_vw }, index) => {
        const has_next = !!breadcrumbs[index + 1];
        const element = to ? (
          <Link neutral to={to}>
            <Text semi ellipsis={!has_next || ellipsis} title={label} style={{ minWidth: 0 }}>
              {label}
            </Text>
          </Link>
        ) : (
          <Text
            semi
            neutral={!highlight}
            ellipsis={!has_next || ellipsis}
            title={label}
            style={{ minWidth: 0 }}>
            {label}
          </Text>
        );

        return [
          <Div key={label} max_w={{ vw: max_width_vw }} style={{ minWidth: 0, overflow: 'hidden' }}>
            {element}
          </Div>,
          has_next && (
            <Text key={`${label}-separator`} semi neutral={!highlight}>
              /
            </Text>
          ),
        ];
      })}
    </Div>
  );
};

const StyledViewTitle = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
);

export const ViewTitle = ({
  title,
  tooltip,
  link,
  link_label,
}: {
  title: string;
  tooltip?: string;
  link?: string;
  link_label?: string;
}) => {
  return (
    <StyledViewTitle>
      <Text semi size="m">
        {title}
      </Text>
      {tooltip && link && (
        <Tooltip
          tooltip={tooltip}
          cta={{
            icon: 'docs',
            label: link_label || `${capitalizeFirstLetter(title.toLowerCase())} docs`,
            to: link,
          }}>
          <Icon icon="info" muted />
        </Tooltip>
      )}
    </StyledViewTitle>
  );
};

export const ViewSidebarTitle = ({ title }: { title: string }) => {
  return (
    <StyledViewTitle>
      <Text semi size="m">
        {title}
      </Text>
    </StyledViewTitle>
  );
};

export const StyledViewNavActions = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(3)};
  `,
);

export const StyledViewSidebarActions = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
  `,
);

export const StyledViewContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 auto;
  min-width: 0;
  min-height: 100%;
`;

// Header row only (breadcrumbs); add VIEW_NAV_SECONDARY_HEIGHT when the tabs row is present.
export const VIEW_NAV_HEADER_HEIGHT = 53;
export const VIEW_NAV_HEIGHT = 106;

export const StyledViewSplitPaneLayout = styled.div<{
  banner_active?: boolean;
  nav_height?: number;
}>(({ theme, banner_active, nav_height = VIEW_NAV_HEIGHT }) => {
  const nav_offset = banner_active ? nav_height + 32 : nav_height;
  return css`
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    min-height: calc(100vh - ${nav_offset}px);
    background-color: ${theme.colors.surface.base.surface};
    --view-split-pane-sticky-top: ${theme.pxToRem(nav_offset)};
    --view-split-pane-height: calc(100vh - ${theme.pxToRem(nav_offset)});
  `;
});

export const StyledViewSplitPaneSidebar = styled.aside(
  ({ theme }) => css`
    position: sticky;
    top: var(--view-split-pane-sticky-top);
    width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    min-width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    max-width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    height: var(--view-split-pane-height);
    max-height: var(--view-split-pane-height);
    /* Top padding is 4px shorter so large title text optically aligns with the content pane. */
    padding: ${theme.spacing(6)};
    box-sizing: border-box;
    border-right: ${theme.border};
    overflow-y: auto;
  `,
);

export const StyledViewSplitPaneFooter = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(6)};
    border-top: ${theme.border};
    background-color: ${theme.colors.surface.base.surface};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${theme.zindex.sticky};
  `,
);

export const StyledViewSplitPaneContent = styled.main(
  ({ theme }) => css`
    flex: 1 1 auto;
    min-width: 0;
    /* Top padding matches the sidebar's shortened top padding so titles line up. */
    padding: ${theme.spacing(6)};
    box-sizing: border-box;
  `,
);

export const ViewMetadataItem = ({ label, children }: { label: string; children: ReactNode }) => (
  <Div grid={{ columns: 1, gap_y: 1 }} style={{ minWidth: 0 }}>
    <Text muted ellipsis>
      {label}
    </Text>
    <Div style={{ minWidth: 0 }}>{children}</Div>
  </Div>
);

export const ViewMetadataDescription = ({ children }: { children: ReactNode }) => (
  <Text style={{ overflowWrap: 'anywhere', whiteSpace: 'normal', minWidth: 0 }}>{children}</Text>
);

// Vertical stack for the split pane sidebar: header + metadata items with even spacing.
export const StyledViewSplitPaneSidebarBody = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
);

const StyledViewSplitPaneSidebarSection = styled.div<{ $bordered?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  ${({ $bordered, theme }) =>
    $bordered
      ? `
          margin-top: ${theme.spacing(2)};
          border-top: ${theme.border};
          padding-top: ${theme.spacing(6)};
        `
      : ''}
`;

export const ViewSplitPaneSidebarSection = ({
  title,
  bordered,
  children,
}: {
  title?: string;
  bordered?: boolean;
  children: ReactNode;
}) => (
  <StyledViewSplitPaneSidebarSection $bordered={bordered}>
    {title && (
      <Text bold size="m">
        {title}
      </Text>
    )}
    {children}
  </StyledViewSplitPaneSidebarSection>
);

export const ViewSplitPaneSidebarHeader = ({
  title,
  action,
  badges,
}: {
  title: string;
  action?: ReactNode;
  badges?: ReactNode;
}) => (
  <Div flex={{ direction: 'column', gap: 2 }} m={{ t: action ? -0.5 : 0 }}>
    <Div flex={{ align: 'center', justify: 'space-between', gap: 4 }}>
      <Text bold size="l">
        {title}
      </Text>
      {action}
    </Div>
    {badges && <Div flex={{ align: 'center', gap: 2, wrap: true }}>{badges}</Div>}
  </Div>
);

export const ViewMetadataCopyable = ({ value, wrap }: { value: string; wrap?: boolean }) => (
  <CopyableText value={value} wrap={wrap} mono />
);

const formatMetadataTimestamp = (date: string, timezone: string) =>
  `${formatDate(date, 'yyyy-MM-dd HH:mm:ss', timezone)} ${getTimezoneAbbreviation(timezone, date)}`;

export const ViewMetadataDate = ({ date }: { date: string }) => {
  const { timezone } = useDatePreferences();

  return (
    <Tooltip tooltip={formatMetadataTimestamp(date, timezone)} side="bottom">
      <Text>{capitalizeFirstLetter(formatDistanceToNow(new Date(date), { addSuffix: true }))}</Text>
    </Tooltip>
  );
};

// Heading row at the top of the split pane content area (title + optional actions).
export const ViewSplitPaneContentHeader = ({
  title,
  elements,
  actions,
}: {
  title: string;
  elements?: ReactNode;
  actions?: ReactNode;
}) => (
  <Div
    flex={{ justify: 'space-between', align: 'center', gap: 4 }}
    m={{ b: 4, t: actions ? -0.5 : 0 }}>
    <Div flex={{ align: 'center', gap: 2 }}>
      <Text as="h2" size="l" bold m={0}>
        {title}
      </Text>
      {elements}
    </Div>
    {actions && <Div flex={{ gap: 3, align: 'center' }}>{actions}</Div>}
  </Div>
);

// Stacked content sections inside the split pane content area. Replaces ad-hoc
// Container + Divider spacing: sections get even vertical rhythm and a separator.
export const StyledViewSplitPaneSection = styled.div<{
  max_width?: keyof typeof CONTAINER_WIDTHS;
}>(
  ({ theme, max_width }) => css`
    width: 100%;
    box-sizing: border-box;
    ${max_width
      ? css`
          > * {
            max-width: ${theme.pxToRem(CONTAINER_WIDTHS[max_width])};
          }
        `
      : ''}
    &:not(:first-of-type) {
      margin-top: ${theme.spacing(8)};
      padding-top: ${theme.spacing(8)};
      border-top: ${theme.border};
    }
  `,
);

export const StyledViewSidebar = styled.div<{
  width?: 'small' | 'large';
}>(({ theme, width }) => {
  const { is_degraded_banner_active } = useContext(DashboardContext);
  const computed_width = width === 'large' ? theme.pxToRem(904) : theme.pxToRem(480);
  return css`
    position: sticky;
    display: flex;
    flex-direction: column;
    top: ${is_degraded_banner_active ? '32px' : '0px'};
    right: 0;

    flex-basis: ${computed_width};
    width: ${computed_width};
    min-width: ${computed_width};
    background-color: ${theme.colors.surface.base.surface};
    border-left: ${theme.border};
    height: calc(100vh - ${is_degraded_banner_active ? '32px' : '0px'});
    overflow-y: auto;
    z-index: ${theme.zindex.sticky};
  `;
});

export const StyledViewSidebarSection = styled(Div)(
  ({ theme }) => css`
    padding: ${theme.spacing(4)};
    &:not(:last-child) {
      border-bottom: ${theme.border};
    }
  `,
);

export const StyledViewSidebarNav = styled(StyledViewNav)`
  top: 0;
  ${StyledViewNavHeader} {
    padding: ${({ theme }) => theme.spacing(0)} ${({ theme }) => theme.spacing(6)}
      ${({ theme }) => theme.spacing(0)} ${({ theme }) => theme.spacing(6)};
  }
`;

export const StyledViewSidebarFooter = styled.div`
  ${({ theme }) => css`
    display: flex;
    padding: ${theme.spacing(3)} ${theme.spacing(6)};
    border-top: ${theme.border};
    background-color: ${theme.colors.surface.base.surface};
  `}
`;

export const StyledViewSidebarScrollableSection = styled.div`
  flex-grow: 1;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;
